/**
 * CR-6724: shared Download Template action.
 *
 * Used by both the upload dialog's column panel and the tile grid, so a user
 * can grab a template without opening the upload flow first.
 */
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { dataImportAPI } from '../../lib/api';
import { apiErrorMessage } from '../../lib/api/errors';

/** Pull the filename out of a Content-Disposition header, if it carries one. */
function filenameFromHeaders(headers: unknown, fallback: string): string {
  const disposition = (headers as Record<string, string> | undefined)?.[
    'content-disposition'
  ];
  const match = disposition?.match(/filename="?([^";]+)"?/);
  return match?.[1] ?? fallback;
}

interface TemplateDownload {
  /** Step currently downloading, or null when idle. */
  downloadingStep: number | null;
  /** Fetch and save the .xlsx template for one import step. */
  downloadTemplate: (step: number, typeName: string) => Promise<void>;
}

/**
 * Download an import template as a file.
 *
 * The endpoint requires a bearer token, so the workbook is fetched through the
 * API client and saved client-side rather than linked to directly.
 *
 * @returns The in-progress step and the download action
 *
 * @example
 * const { downloadingStep, downloadTemplate } = useTemplateDownload();
 * <Button onClick={() => downloadTemplate(7, 'GRNs')} />
 */
export function useTemplateDownload(): TemplateDownload {
  const [downloadingStep, setDownloadingStep] = useState<number | null>(null);

  const downloadTemplate = useCallback(
    async (step: number, typeName: string) => {
      setDownloadingStep(step);
      try {
        const res = await dataImportAPI.downloadTemplate(step);
        const url = window.URL.createObjectURL(res.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = filenameFromHeaders(
          res.headers, `helix-import-${step}.xlsx`,
        );
        link.click();
        window.URL.revokeObjectURL(url);
        toast.success(`${typeName} template downloaded`);
      } catch (err: unknown) {
        toast.error(apiErrorMessage(err, 'Could not download the template'));
      } finally {
        setDownloadingStep(null);
      }
    },
    [],
  );

  return { downloadingStep, downloadTemplate };
}
